import type { TenantConfig } from "@waypoint/shared";

type ThemeTenant = Pick<TenantConfig, "colors" | "theme">;

/**
 * Apply a tenant's theme as CSS custom properties on the document root, so
 * every tenant — including config-only tenants not present in the static
 * `[data-tenant=…]` CSS — is themed from its config:
 *
 * - `--tenant-primary` / `--color-primary` ← colors.primary
 * - `--tenant-secondary` ← colors.accent (the tenant brand "secondary")
 * - `--tenant-font-headline` ← theme.headlineFont (optional)
 * - `--tenant-font-body` ← theme.bodyFont (optional)
 * - any additional theme.cssVariables verbatim (escape hatch for vars like
 *   `--primary` / `--ring` / `--color-accent` whose per-tenant values are not
 *   derivable from `colors`).
 *
 * Note: `--color-accent` is intentionally NOT derived from `colors.accent` —
 * they are distinct legacy variables (e.g. BYU's `--color-accent` is a blue,
 * not its white brand accent). Set it via `theme.cssVariables` when needed.
 */
export const applyTenantTheme = (tenant: ThemeTenant, root?: HTMLElement): void => {
  const el = root ?? (typeof document !== "undefined" ? document.documentElement : undefined);
  if (!el) return;

  const { colors, theme } = tenant;
  if (colors?.primary) {
    el.style.setProperty("--tenant-primary", colors.primary);
    el.style.setProperty("--color-primary", colors.primary);
  }
  if (colors?.accent) {
    el.style.setProperty("--tenant-secondary", colors.accent);
  }
  if (theme?.headlineFont) {
    el.style.setProperty("--tenant-font-headline", theme.headlineFont);
  }
  if (theme?.bodyFont) {
    el.style.setProperty("--tenant-font-body", theme.bodyFont);
  }
  for (const [key, value] of Object.entries(theme?.cssVariables ?? {})) {
    if (key.startsWith("--") && value) el.style.setProperty(key, value);
  }
};
